import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { AUTH_BASE_URL } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const isCheckingAuth = useRef(false);

  const checkAuth = async () => {
    // Prevent concurrent auth checks
    if (isCheckingAuth.current) {
      console.log('Auth check already in progress, skipping');
      return;
    }
    
    isCheckingAuth.current = true;
    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/me`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.log('Auth check returned:', response.status);
        if (response.status === 401) {
          // Normal case - user not authenticated
          setUser(null);
          setAuthError(null);
        } else {
          // Server error - don't keep retrying
          setAuthError(`Server returned ${response.status}`);
          setUser(null);
        }
        return;
      }
      
      const data = await response.json();
      setUser(data.user);
      setAuthError(null);
    } catch (error) {
      console.error('Auth check failed:', error);
      // Network error - don't keep retrying
      setAuthError(error.message);
      setUser(null);
    } finally {
      setLoading(false);
      isCheckingAuth.current = false;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${AUTH_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value = {
    user,
    loading,
    authError,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};